'use client'

import {
  createContext,
  type FC,
  type PropsWithChildren,
  useContext,
  useMemo,
} from 'react'

import { type GkAirProxyServiceWebResponseRecommendationsValue } from '@redteclab/api/clients/crosssellers-proxy-service'
import { type Metadata } from '@redteclab/api/clients/retail-media-xsell-service'

import { useGlobalConfigContext } from '../../../global-config'
import { urlResolverGetSearchPage } from '../../../url-handling'
import { type GkAirAreaType } from '../../config/GkAirAreaType'
import { type GkAirServiceType } from '../../config/GkAirServiceType'
import { gkAirAccessorGetAreaTitle } from '../../model/gkAirAccessorGetAreaTitle'

interface GkAirAreaContextValue {
  areaId?: GkAirAreaType
  areaTitle?: string
  // will be extended by Adserver people as it is used by Adserver
  metadata?: Metadata
  moreHref?: string
  serviceId?: GkAirServiceType
}

interface GkAirAreaProviderProps {
  areaData?: GkAirProxyServiceWebResponseRecommendationsValue
  areaId: GkAirAreaType
  metadata?: Metadata
  serviceId: GkAirServiceType
}

const Context = createContext<GkAirAreaContextValue>({})

export const useGkAirAreaContext = (): GkAirAreaContextValue =>
  useContext(Context)

export const GkAirAreaProvider: FC<
  PropsWithChildren<GkAirAreaProviderProps>
> = ({ areaData, areaId, children, metadata, serviceId }) => {
  const globalConfig = useGlobalConfigContext()
  const areaTitle = gkAirAccessorGetAreaTitle({ areaData })
  // to be extended by Adserver people as it is used by Adserver

  const moreHref = useMemo(
    () =>
      areaData?.content
        ? urlResolverGetSearchPage(globalConfig, {
            queryParams: {
              // eslint-disable-next-line id-length -- search param
              q: areaData.content.map(({ data }) => data.UID).join(','),
            },
          })
        : undefined,
    [areaData?.content, globalConfig],
  )

  const value = useMemo(
    () => ({
      areaId,
      areaTitle,
      metadata,
      moreHref,
      serviceId,
    }),
    [areaTitle, moreHref, areaId, metadata, serviceId],
  )

  return <Context.Provider value={value}>{children}</Context.Provider>
}
